/**
 * 시민 등록의 **연락처·동의** — 08-21 회의 C-8이 정한 것을 화면 쪽에서 지키는 자리다.
 * 옛 설계서 C-01의 「1단계 개인정보 0개」를 그 결정이 뒤집었다.
 *
 * - 이름은 **필수**, 닿을 수단은 **휴대폰·이메일 택1**
 * - 동의는 **아래 목록 전부** — 서버 `REQUIRED_CITIZEN_CONSENTS`(`apps/api/src/lib/consent.ts`)가 정본이다
 *
 * **검증은 여기서 끝나지 않는다** — 서버가 같은 규칙을 다시 본다(400). 여기 있는 것은
 * 「보내기 전에 알려 주기」이지 방어선이 아니다.
 */

const PHONE_DIGITS = /^01[016789]\d{7,8}$/

function digitsOf(value: string): string {
  return value.replace(/\D/g, '')
}

/** 입력 중 하이픈을 넣어 준다. 끝에는 붙이지 않는다(지우기가 어려워진다). */
export function formatPhone(value: string): string {
  const d = digitsOf(value).slice(0, 11)
  if (d.length <= 3) return d
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
  const mid = d.length === 11 ? 7 : 6
  return `${d.slice(0, 3)}-${d.slice(3, mid)}-${d.slice(mid)}`
}

/** 형식이 맞는가. **빈 값은 「틀렸다」가 아니다** — 필수 여부는 따로 본다. */
export function isPhoneValid(value: string): boolean {
  const d = digitsOf(value)
  return d.length === 0 || PHONE_DIGITS.test(d)
}

/** 이메일 형식. 휴대폰과 같은 규칙으로 **빈 값은 오류가 아니다.** */
export function isEmailValid(value: string): boolean {
  return value.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

/**
 * 필수 동의 둘. **개수를 숫자로 박지 않는다** — 항목이 늘면 제출 조건도 함께 늘어야 한다.
 *
 * 문구는 개인정보 처리방침(v1, 시행 2026-08-06)에서 끌어왔다. 키는 서버가 받는
 * `['privacy','disclose']`뿐이고 `publish`를 보내면 400이다(2026-08-25 실측).
 */
export const CONSENTS = [
  {
    key: 'privacy',
    label: '개인정보 수집·이용 동의 (필수)',
    desc: '이름·연락처를 수집합니다. 운영기관이 직접 연락드리는 데만 쓰고, 보관 기간은 1년이며 언제든 철회하실 수 있습니다.',
  },
  {
    /**
     * 공개 동의가 **필수인 이유**는 공개되지 않는 카드가 매칭에도 지도에도 쓰이지 않아서다
     * (CLAUDE.md §4-12).
     */
    key: 'disclose',
    label: '문제 내용 공개 동의 (필수)',
    desc: '등록하신 문제가 공개 화면과 지도에 실립니다. 연락처는 공개되지 않습니다.',
  },
] as const

export type ConsentKey = (typeof CONSENTS)[number]['key']

/** 닿을 수단 — 한 번에 하나만 받는다(둘 다 받으면 어느 쪽으로 연락할지 모호해진다). */
export type ContactChannel = 'phone' | 'email'
